/* eslint-disable no-console */
import libphonenumber from "google-libphonenumber";

import {
  AETNA_PARTNER_ID,
  B2C_PARTNERS,
  BVSD_PARTNER_ID,
  CAREFIRST_PARTNER_ID,
  HIGHMARK_PARTNERS,
  MEMBER_TERMINOLOGY_PARTNERS,
} from "@/constants/Partners";
import type { EligibilityEnforcementType, Partner } from "@/models";

/**
 * Formats the partner's custom support link into a more user friendly string.
 * Supports https, mailto, and tel URIs
 *
 * Examples:
 *
 * "https://www.noom.com" -> "https://www.noom.com"
 *
 * "mailto:partnersupport@noom.com" -> "partnersupport@noom.com"
 *
 * "tel:+18882665071" -> "(888) 266-5071"
 *
 * @param supportLink custom support link for the partner
 * @returns formatted support link, or empty string
 */
export const formatCustomSupportLink = (supportLink: string) => {
  const parts = supportLink.split(":", 2);

  if (parts.length !== 2) {
    console.error(`Invalid custom support link: ${supportLink}`);
    return "";
  }

  const [scheme, value] = parts;

  if (scheme === "https") {
    return supportLink;
  }

  if (scheme === "mailto") {
    return value;
  }

  if (scheme === "tel") {
    const phoneNumberUtil = libphonenumber.PhoneNumberUtil.getInstance();
    const PNF = libphonenumber.PhoneNumberFormat;

    try {
      const number = phoneNumberUtil.parse(supportLink, "US");
      return phoneNumberUtil.format(number, PNF.NATIONAL);
    } catch (error) {
      console.error(`Invalid phone number: ${supportLink}`, error);
      return "";
    }
  }

  console.error(`Unsupported URI scheme: ${supportLink}`);
  return "";
};

export const isB2CPartner = (partnerInfo: Partner | null) =>
  B2C_PARTNERS.includes(partnerInfo?.id as number);

export const isBVSDPartner = (partnerInfo: Partner | null) =>
  partnerInfo?.id === BVSD_PARTNER_ID;

export const isCareFirstPartner = (partnerInfo: Partner | null) =>
  partnerInfo?.id === CAREFIRST_PARTNER_ID;

export const isAetnaPartner = (partnerInfo: Partner | null) =>
  partnerInfo?.id === AETNA_PARTNER_ID;

/**
 * Returns true if the partner sources eligibility exclusively through SAML
 * assertions (no eligibility file). Such partners can only enroll via a SAML
 * hand-off, which supplies a pre-populated eligibilityId.
 */
export const isSamlOnlyPartner = (partnerInfo: Partner | null) =>
  partnerInfo?.configs?.ELIGIBILITY_SOURCE === "SAML_ASSERTION";

const DOB_ENFORCEMENT_TYPES: EligibilityEnforcementType[] = [
  "LN_DOB",
  "FULL_NAME_DOB",
  "UHC_OPTUM_BIG_FIVE",
];

/** True if the upfront enforcement type collects/validates date of birth. */
export const enforcementRequiresDob = (
  enforcement?: EligibilityEnforcementType,
): boolean => !!enforcement && DOB_ENFORCEMENT_TYPES.includes(enforcement);

/**
 * True if DOB is required for the partner — either explicitly via the REQUIRES_DOB
 * option, or because upfront eligibility enforcement collects/validates DOB. When
 * neither is true, a missing DOB should not block enrollment.
 */
export const isDobRequired = (partnerInfo: Partner | null): boolean =>
  partnerInfo?.configs?.REQUIRES_DOB === true ||
  enforcementRequiresDob(partnerInfo?.configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT);

export const isCmsAccessPartner = (partnerInfo: Partner | null): boolean =>
  partnerInfo?.configs?.OFFERS_CMS_ACCESS === true;

/**
 * True when the CMS ACCESS enrollment experience should be shown: the partner
 * has the OFFERS_CMS_ACCESS config, or (local development only) the URL has
 * `?cmsAccess=true`.
 *
 * TODO(VIBE-1169): drop the query param override once a partner with the
 * OFFERS_CMS_ACCESS config exists in test.
 */
export const isCmsAccessEnabled = (partnerInfo: Partner | null): boolean =>
  isCmsAccessPartner(partnerInfo) ||
  (window.ENV.NOOM_ENVIRONMENT === "development" &&
    new URLSearchParams(window.location.search).get("cmsAccess") === "true");

/**
 * Checks if the partner is a Highmark partner based on the list of Highmark
 * partner IDs
 *
 * @param partnerInfo partner information
 * @returns true if the partner is a Highmark partner, false otherwise
 */
export const isHighmarkPartner = (partnerInfo: Partner | null) => {
  if (!partnerInfo) {
    return false;
  }

  return HIGHMARK_PARTNERS.includes(partnerInfo.id);
};

/**
 * Returns true if the partner refers to their participants as "members" rather
 * than "employees". Affects field labels, placeholders, and error messages.
 */
export const shouldReferToParticipantsAsMembers = (
  partnerInfo: Partner | null,
) =>
  partnerInfo?.configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT ===
    "UHC_OPTUM_BIG_FIVE" ||
  (partnerInfo?.id != null &&
    MEMBER_TERMINOLOGY_PARTNERS.includes(partnerInfo.id));
